/**
 * useUpdateStatus — React hook that surfaces auto-update lifecycle
 * (M5 / Step 8).
 *
 * On web the bridge's updates surface is a no-op (`enabled: false`); the
 * hook still mounts cleanly and just returns `{ phase: 'idle' }`. On
 * desktop it pulls the current status once on mount and then subscribes
 * to live status broadcasts from the main process.
 */

import { useCallback, useEffect, useState } from 'react';
import type { UpdateStatusSnapshot } from '@forgedas/shared-core';
import { platformBridge } from '../services/platformBridge';

const IDLE: UpdateStatusSnapshot = { phase: 'idle' };

export interface UseUpdateStatus {
  status: UpdateStatusSnapshot;
  enabled: boolean;
  applyUpdate: () => Promise<void>;
  checkForUpdates: () => Promise<void>;
}

export function useUpdateStatus(): UseUpdateStatus {
  const [status, setStatus] = useState<UpdateStatusSnapshot>(IDLE);
  const enabled = platformBridge.updates.enabled;

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    void platformBridge.updates.getStatus().then((snap) => {
      if (!cancelled) setStatus(snap);
    });

    const unsubscribe = platformBridge.updates.onStatus((next) => {
      if (!cancelled) setStatus(next);
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [enabled]);

  const applyUpdate = useCallback(async () => {
    if (!enabled) return;
    await platformBridge.updates.quitAndInstall();
  }, [enabled]);

  const checkForUpdates = useCallback(async () => {
    if (!enabled) return;
    await platformBridge.updates.checkForUpdates();
  }, [enabled]);

  return { status, enabled, applyUpdate, checkForUpdates };
}
